import { useEffect, useRef, useMemo, useCallback, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { Empty, Spin } from 'antd'
import type { Message } from '@interfaces/chat.interface'
import { MessageItem } from '../MessageItem'
import { formatDateSeparator, isSameDay } from '@utils/formatters'
import styles from './styles.module.less'

interface MessageListProps {
  messages: Message[]
  isLoading?: boolean
  onDownload?: (url: string) => void
  onLoadMore?: () => void
  hasMore?: boolean
  pageId?: string | null
}

export const MessageList = ({ messages, isLoading, onDownload, onLoadMore, hasMore, pageId }: MessageListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevMessagesRef = useRef<Message[]>([]);
  const prevIsLoadingRef = useRef(isLoading);
  const conversationIdRef = useRef<string | undefined>(undefined);
  const hasCheckedAutoLoadRef = useRef(false);
  const [isAtTop, setIsAtTop] = useState(false);

  const sortedMessages = useMemo(
    () =>
      [...messages].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ),
    [messages]
  );

  const currentConversationId = sortedMessages[0]?.conversationId;

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  const performScroll = useCallback(() => {
    scrollToBottom();
    requestAnimationFrame(() => {
      scrollToBottom();
      [0, 50, 100, 200, 300, 500].forEach((delay) =>
        setTimeout(scrollToBottom, delay)
      );
    });
    [0, 100, 300, 500, 800].forEach((delay) => setTimeout(scrollToBottom, delay));
  }, [scrollToBottom]);

  useEffect(() => {
    if (sortedMessages.length === 0) return;

    const conversationChanged = currentConversationId !== conversationIdRef.current;
    const prevMessages = prevMessagesRef.current;
    const prevLength = prevMessages.length;
    const currentLength = messages.length;
    
    const isLoadMore = 
      currentLength > prevLength && 
      prevLength > 0 &&
      messages[0]?.id !== prevMessages[0]?.id &&
      messages[messages.length - 1]?.id === prevMessages[prevMessages.length - 1]?.id;
    const isNewMessage = 
      currentLength > prevLength && 
      prevLength > 0 &&
      messages[0]?.id === prevMessages[0]?.id &&
      messages[messages.length - 1]?.id !== prevMessages[prevMessages.length - 1]?.id;
    
    const loadingFinished = prevIsLoadingRef.current && !isLoading;
    const initialLoad = prevLength === 0 && currentLength > 0;
    const shouldScroll =
      conversationChanged ||
      isNewMessage ||
      initialLoad ||
      (loadingFinished && !isLoadMore);

    if (shouldScroll) {
      performScroll();
    }

    prevMessagesRef.current = [...messages];
    conversationIdRef.current = currentConversationId;
    prevIsLoadingRef.current = isLoading;

    if (conversationChanged) {
      hasCheckedAutoLoadRef.current = false;
    }
  }, [messages, sortedMessages.length, currentConversationId, isLoading, performScroll]);

  useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      requestAnimationFrame(() => {
        if (container) {
          setIsAtTop(container.scrollTop <= 50);
        }
      });
    }
  }, [currentConversationId]);

  useEffect(() => {
    if (!hasMore || !onLoadMore) return;
    if (sortedMessages.length === 0) return;

    if (prevIsLoadingRef.current && !isLoading) {
      hasCheckedAutoLoadRef.current = false;
    }

    if (isLoading || hasCheckedAutoLoadRef.current) return;

    const checkAndLoadMore = () => {
      const container = scrollRef.current;
      if (!container) return;

      const needsMoreContent = container.scrollHeight <= container.clientHeight + 50;
      
      if (needsMoreContent && hasMore && !isLoading) {
        hasCheckedAutoLoadRef.current = true;
        onLoadMore();
      } else if (!needsMoreContent) {
        hasCheckedAutoLoadRef.current = true;
      }
    };

    const timer = setTimeout(checkAndLoadMore, 150);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(checkAndLoadMore, 50);
      });
    });

    return () => clearTimeout(timer);
  }, [sortedMessages.length, hasMore, isLoading, onLoadMore]);

  const groupedMessages = useMemo(() => {
    const result: Array<Message & { showAvatar: boolean; isOwn: boolean; showDateSeparator?: boolean; dateSeparator?: string; showAgentSeparator?: string }> = [];
    
    sortedMessages.forEach((message, index) => {
      const prevMessage = index > 0 ? sortedMessages[index - 1] : null;
      const isOwn = message.meta?.direction === 'OUTBOUND';
      const prevIsOwn = prevMessage?.meta?.direction === 'OUTBOUND';
      const timeDiff = prevMessage
        ? new Date(message.createdAt).getTime() -
          new Date(prevMessage.createdAt).getTime()
        : Infinity;

      const showDateSeparator = !prevMessage || !isSameDay(message.createdAt, prevMessage.createdAt);
      // Show actorSource if it has a value
      const showAgentSeparator = message.meta?.actorSource as string || '';

      result.push({
        ...message,
        showAvatar: !prevMessage || prevIsOwn !== isOwn || timeDiff > 5 * 60 * 1000,
        isOwn,
        showDateSeparator,
        dateSeparator: showDateSeparator ? formatDateSeparator(message.createdAt) : undefined,
        showAgentSeparator,
      });
    });

    return result;
  }, [sortedMessages]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const threshold = 50; 
      setIsAtTop(scrollTop <= threshold);
      if (scrollTop === 0 && hasMore && !isLoading) {
        onLoadMore?.();
      }
    },
    [hasMore, onLoadMore, isLoading]
  );

  if (isLoading && !messages.length) {
    return (
      <div className={styles.placeholder}>
        <Spin />
      </div>
    );
  }

  if (!sortedMessages.length) {
    return (
      <div className={styles.placeholder}>
        <Empty description="Chưa có tin nhắn" />
      </div>
    );
  }

  return (
    <div className={styles.messageList}>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AutoSizer>
          {({ width, height }: { width: number; height: number }) => (
            <div
              ref={scrollRef}
              style={{
                height: height || 600,
                width: width || '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              className={styles.scrollContainer}
              onScroll={handleScroll}
            >
              <div className={styles.messagesContainer}>
                {isLoading && hasMore && isAtTop && (
                  <div className={styles.loadingMore}>
                    <Spin size="small" />
                  </div>
                )}
                {groupedMessages.map((message) => (
                  <div key={message.id}>
                    {message.showDateSeparator && (
                      <div className={styles.dateSeparator}>
                        {message.dateSeparator}
                      </div>
                    )}
                    <div
                      className={styles.messageWrapper}
                      data-is-own={message.isOwn}
                    >
                      <MessageItem
                        pageId={pageId}
                        message={message}
                        isOwn={message.isOwn}
                        onDownload={onDownload}
                        showAgentSeparator={message.showAgentSeparator}
                        showAvatar={message.showAvatar}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </AutoSizer>
      </div>
    </div>
  );
};

